import { useContext, useMemo } from 'react';
import ResultsContext from '../store/results-context';
import {calculateAge} from '../utils/calculate-age';
import {addVaccinations} from '../utils/add-vaccinations';
import {removeContraindicatedVaccinations} from '../utils/remove-contraindicated-vaccinations';
import {MergedResultsModel} from '../model/merged-results.model';
import {VaccineModel} from '../model/vaccine.model';
import {deduplicate} from '../utils/deduplicate-vaccinations';
import {useTranslation} from 'react-i18next';
import {subtractRepeatedVaccinations} from '../utils/subtract-repeated-vaccinations';
import {AgeRange} from '../utils/age-range';
import {removeFlu} from "../utils/remove-flu";

export const useMergedResults = () => {

    const resultsContext = useContext(ResultsContext);
    const { t } = useTranslation();

    const mergedData = useMemo(() => {

        let mandatoryVaccinations: VaccineModel[] = [];
        let recommendedVaccinations: VaccineModel[] = [];
        let highlyRecommendedVaccinations: VaccineModel[] = [];
        let contraindicatedVaccinations: VaccineModel[] = [];

        const allFiltersUsed: (keyof MergedResultsModel)[] = [
            'pregnancy',
            'plannedPregnancy',
            'circulatorySystemDisease',
            'diabetes',
            'chronicRespiratoryDisease',
            'chronicKidneyDisease',
            'chronicLiverDisease',
            'viralHepatitisC',
            'depression',
            'neoplasticDisease',
            'spleenDysfunction',
            'immuneDisorders',
            'immunosuppressiveDrugs',
            'hivInfection',
            'boneMarrowTransplantationBefore',
            'boneMarrowTransplantationAfter',
            'organTransplantationBefore',
            'organTransplantationAfter',
            'cochlearImplantation',
            'cerebroSpinalFluidLeak',
            'autoimmuneDisease',
            'medicalStudentHealthcare',
            'neonatologyPediatricsWorker',
            'infectiousDiseasesGastroenterologyWorker',
            'workingWithSevereImmunodeficiency',
            'policeman',
            'fireFighter',
            'borderGuard',
            'army',
            'healthCareHomeResident',
            'otherMultiPersonPlace',
            'outdoorActivity',
            'hepatitisBExposure',
            'cigarettesAlcoholAbuse'
        ];

        const ageRange: string = calculateAge(resultsContext.dateOfBirth);

        switch (ageRange) {
            case AgeRange.birthDate_18_to_49:
                resultsContext.on_birthDate_18_to_49(true);
                break;
            case AgeRange.birthDate_50_to_54:
                resultsContext.on_birthDate_50_to_54(true);
                break;
            case AgeRange.birthDate_55_to_59:
                resultsContext.on_birthDate_55_to_59(true);
                break;
            case AgeRange.birthDate_60_to_64:
                resultsContext.on_birthDate_60_to_64(true);
                break;
            case AgeRange.birthDate_over_65:
                resultsContext.on_birthDate_over_65(true);
                break;
            default:
                break;
        }

        addVaccinations(
            mandatoryVaccinations,
            recommendedVaccinations,
            highlyRecommendedVaccinations,
            contraindicatedVaccinations,
            ageRange,
            t
        );

        allFiltersUsed.forEach((condition) => {
            if (resultsContext[condition]) {
                addVaccinations(
                    mandatoryVaccinations,
                    recommendedVaccinations,
                    highlyRecommendedVaccinations,
                    contraindicatedVaccinations,
                    condition.toString(),
                    t
                );
            }
        });

        contraindicatedVaccinations = deduplicate(contraindicatedVaccinations);
        mandatoryVaccinations = deduplicate(mandatoryVaccinations);
        highlyRecommendedVaccinations = deduplicate(highlyRecommendedVaccinations);
        recommendedVaccinations = deduplicate(recommendedVaccinations);

        contraindicatedVaccinations = removeFlu(contraindicatedVaccinations);
        mandatoryVaccinations = removeFlu(mandatoryVaccinations);
        highlyRecommendedVaccinations = removeFlu(highlyRecommendedVaccinations);
        recommendedVaccinations = removeFlu(recommendedVaccinations);

        mandatoryVaccinations = removeContraindicatedVaccinations(mandatoryVaccinations, contraindicatedVaccinations);
        highlyRecommendedVaccinations = removeContraindicatedVaccinations(highlyRecommendedVaccinations, contraindicatedVaccinations);
        recommendedVaccinations = removeContraindicatedVaccinations(recommendedVaccinations, contraindicatedVaccinations);

        highlyRecommendedVaccinations = subtractRepeatedVaccinations(highlyRecommendedVaccinations, mandatoryVaccinations);
        recommendedVaccinations = subtractRepeatedVaccinations(recommendedVaccinations, mandatoryVaccinations);
        recommendedVaccinations = subtractRepeatedVaccinations(recommendedVaccinations, highlyRecommendedVaccinations);

        return {
            mandatoryVaccinations,
            recommendedVaccinations,
            highlyRecommendedVaccinations,
            contraindicatedVaccinations
        };
    }, [resultsContext, t]);

    return mergedData;
};
